import { useState } from 'react'
import {
  ChevronDown, ChevronUp, ChevronRight, SlidersHorizontal, Lock, Unlock, Power, KeyRound,
  Plus, X, Info, Check, RotateCcw,
} from 'lucide-react'
import { labelCls, inputCls } from '../styles'
import {
  DEFAULT_PORTAL_USERS, DEFAULT_WEBHOOK_CONFIG, DEFAULT_API_KEYS, DEFAULT_SECRETS, DEFAULT_APPROVAL_NAMESPACES,
} from '../data'
import type {
  ApiKeyConfig, ApprovalNamespaceConfig, PortalUserConfig, SecretConfig, WebhookConfig,
} from '../types'

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer shrink-0">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
      <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: checked ? '#6366f1' : '#e5e7eb' }} />
      <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
    </label>
  )
}

function SubSection({ title, description, children, defaultOpen = false }: { title: string; description: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[#1e293b]">{title}</p>
          <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{description}</p>
        </div>
      </button>
      {open && <div className="p-3 space-y-3" style={{ borderTop: '1px solid #e5e7eb' }}>{children}</div>}
    </div>
  )
}

let idCounter = 0
function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

export function PlatformConfigPanel() {
  const [open, setOpen] = useState(false)

  const [users, setUsers] = useState<PortalUserConfig[]>(DEFAULT_PORTAL_USERS)
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig>(DEFAULT_WEBHOOK_CONFIG)
  const [testedEndpoint, setTestedEndpoint] = useState<string | null>(null)
  const [apiKeys, setApiKeys] = useState<ApiKeyConfig[]>(DEFAULT_API_KEYS)
  const [secrets, setSecrets] = useState<SecretConfig[]>(DEFAULT_SECRETS)
  const [namespaces, setNamespaces] = useState<ApprovalNamespaceConfig[]>(DEFAULT_APPROVAL_NAMESPACES)
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  function toggleUserActive(id: string) {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: !u.active } : u)))
  }
  function toggleUserLocked(id: string) {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, locked: !u.locked } : u)))
  }
  function addUser() {
    setUsers((prev) => [...prev, {
      id: nextId('user'), name: 'New User', email: 'new.user@example.com', active: true, locked: false,
      accountType: 'Child User', roles: ['App-Role-CustomerService'], failedAttempts: 0, createdDate: new Date().toISOString().slice(0, 10),
    }])
  }
  function removeUser(id: string) {
    setUsers((prev) => prev.filter((u) => u.id !== id))
  }

  function addEndpoint() {
    setWebhookConfig((c) => ({ ...c, endpoints: [...c.endpoints, { id: nextId('endpoint'), url: '' }] }))
  }
  function updateEndpoint(id: string, url: string) {
    setWebhookConfig((c) => ({ ...c, endpoints: c.endpoints.map((e) => (e.id === id ? { ...e, url } : e)) }))
  }
  function removeEndpoint(id: string) {
    setWebhookConfig((c) => ({ ...c, endpoints: c.endpoints.filter((e) => e.id !== id) }))
  }
  function testEndpoint(id: string) {
    setTestedEndpoint(id)
    setTimeout(() => setTestedEndpoint((cur) => (cur === id ? null : cur)), 2000)
  }

  function addApiKey() {
    const today = new Date().toISOString().slice(0, 10)
    setApiKeys((prev) => [...prev, { id: nextId('key'), label: 'New Key', tag: 'Current', createdDate: today, activatedDate: today, expiresDate: '—' }])
  }
  function removeApiKey(id: string) {
    setApiKeys((prev) => prev.filter((k) => k.id !== id))
  }

  function addSecret() {
    setSecrets((prev) => [...prev, { id: nextId('secret'), reference: 'new_secret_ref', keys: ['KEY_NAME'] }])
  }
  function removeSecret(id: string) {
    setSecrets((prev) => prev.filter((s) => s.id !== id))
  }

  function approveNamespace(id: string) {
    setNamespaces((prev) => prev.map((n) => (n.id === id ? { ...n, latestApproved: n.activeVersion, status: 'APPROVED' } : n)))
    setReviewingId(null)
  }
  function rejectNamespace(id: string) {
    setReviewingId((cur) => (cur === id ? null : cur))
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Platform Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569]">
            Account-level settings from Operations Center that sit alongside the product configs — Portal users, Webhooks, API Access &amp; Secrets, and the Approvals/Review workflow for namespace/connector versions.
          </p>

          <SubSection title="Portal" description={`${users.length} portal user${users.length !== 1 ? 's' : ''} for this customer`} defaultOpen>
            <div className="space-y-2">
              {users.map((user) => (
                <div key={user.id} className="rounded-[9px] p-3 space-y-2" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-[#1e293b]">{user.name}</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" style={user.active ? { background: 'rgba(52,211,153,.15)', color: '#15803d' } : { background: 'rgba(148,163,184,.15)', color: '#475569' }}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                    {user.locked && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: 'rgba(248,113,113,.15)', color: '#b91c1c' }}>Locked</span>
                    )}
                    <span className="text-[9px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,.12)', color: '#4f46e5' }}>{user.accountType}</span>
                  </div>
                  <p className="text-[10px] text-[#64748b]">{user.email} · Created {user.createdDate} · Failed attempts: {user.failedAttempts}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.roles.map((role) => (
                      <span key={role} className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>{role}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={() => toggleUserActive(user.id)} className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[10px] font-semibold transition-colors" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: user.active ? '#b91c1c' : '#15803d' }}>
                      <Power size={10} /> {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => toggleUserLocked(user.id)} className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[10px] font-semibold transition-colors" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                      {user.locked ? <Unlock size={10} /> : <Lock size={10} />} {user.locked ? 'Unlock' : 'Lock'}
                    </button>
                    <button onClick={() => removeUser(user.id)} className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[10px] font-semibold transition-colors ml-auto" style={{ background: 'transparent', color: '#b91c1c' }}>
                      <X size={10} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addUser} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-[8px] text-[#64748b] hover:text-blue-700 border border-dashed border-[#e5e7eb] hover:border-blue-600 transition-colors">
              <Plus size={12} /> Add user
            </button>
          </SubSection>

          <SubSection title="Webhooks" description="Notification settings and destinations for entity/workflow events">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">Notifications</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Whether webhook notifications are sent for entity and workflow events at all.</p>
              </div>
              <Toggle checked={webhookConfig.notificationsEnabled} onChange={() => setWebhookConfig((c) => ({ ...c, notificationsEnabled: !c.notificationsEnabled }))} />
            </div>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">JWT Security</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">JWT signing applied to all HTTP webhook endpoints, so receivers can verify the payload came from FrankieOne.</p>
              </div>
              <Toggle checked={webhookConfig.jwtSecurityEnabled} onChange={() => setWebhookConfig((c) => ({ ...c, jwtSecurityEnabled: !c.jwtSecurityEnabled }))} />
            </div>

            <div>
              <label className={labelCls}>Notification endpoints · {webhookConfig.endpoints.length}</label>
              <div className="space-y-1.5">
                {webhookConfig.endpoints.map((ep) => (
                  <div key={ep.id} className="flex items-center gap-2">
                    <span className="text-[9px] font-bold px-1.5 py-1 rounded shrink-0" style={{ background: 'rgba(99,102,241,.12)', color: '#4f46e5' }}>HTTPS</span>
                    <input className={inputCls} value={ep.url} placeholder="https://your-server.com/webhooks/frankieone" onChange={(e) => updateEndpoint(ep.id, e.target.value)} />
                    <button onClick={() => testEndpoint(ep.id)} className="px-2.5 py-1.5 rounded-[7px] text-[10px] font-semibold shrink-0" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: testedEndpoint === ep.id ? '#15803d' : '#475569' }}>
                      {testedEndpoint === ep.id ? <span className="flex items-center gap-1"><Check size={10} /> Tested</span> : 'Test'}
                    </button>
                    <button onClick={() => removeEndpoint(ep.id)} className="text-[#64748b] hover:text-red-700 shrink-0"><X size={13} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addEndpoint} className="flex items-center gap-1.5 px-2.5 py-1.5 mt-2 text-[11px] rounded-[8px] text-[#64748b] hover:text-blue-700 border border-dashed border-[#e5e7eb] hover:border-blue-600 transition-colors">
                <Plus size={12} /> Add endpoint
              </button>
              <p className="text-[10px] text-[#475569] mt-1.5 flex items-center gap-1.5"><Info size={11} className="shrink-0" /> Test is simulated — no real HTTP request is sent from this demo.</p>
            </div>
          </SubSection>

          <SubSection title="API Access & Secrets" description={`${apiKeys.length} API key${apiKeys.length !== 1 ? 's' : ''}, ${secrets.length} secret${secrets.length !== 1 ? 's' : ''}`}>
            <div>
              <label className={labelCls}>API Keys</label>
              <div className="space-y-1.5">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <KeyRound size={13} className="text-[#4f46e5] shrink-0" />
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={{ background: 'rgba(251,146,60,.15)', color: '#c2410c' }}>{key.tag}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-[#1e293b]">{key.label}</p>
                      <p className="text-[10px] text-[#64748b]">Created {key.createdDate} · Activated {key.activatedDate} · Expires {key.expiresDate}</p>
                    </div>
                    <button onClick={() => removeApiKey(key.id)} className="text-[#64748b] hover:text-red-700 shrink-0"><X size={13} /></button>
                  </div>
                ))}
                {apiKeys.length === 0 && <p className="text-[10px] text-[#475569] italic">No API keys configured for this customer.</p>}
              </div>
              <button onClick={addApiKey} className="flex items-center gap-1.5 px-2.5 py-1.5 mt-2 text-[11px] rounded-[8px] text-[#64748b] hover:text-blue-700 border border-dashed border-[#e5e7eb] hover:border-blue-600 transition-colors">
                <Plus size={12} /> Create API key
              </button>
            </div>

            <div>
              <label className={labelCls}>Secrets</label>
              <div className="space-y-1.5">
                {secrets.map((secret) => (
                  <div key={secret.id} className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Lock size={13} className="text-[#4f46e5] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-mono font-semibold text-[#1e293b]">{secret.reference}</p>
                      <p className="text-[10px] text-[#64748b]">{secret.keys.length} key{secret.keys.length !== 1 ? 's' : ''}: {secret.keys.join(', ')}</p>
                    </div>
                    <button onClick={() => removeSecret(secret.id)} className="text-[#64748b] hover:text-red-700 shrink-0"><X size={13} /></button>
                  </div>
                ))}
                {secrets.length === 0 && <p className="text-[10px] text-[#475569] italic">No secrets configured for this customer.</p>}
              </div>
              <button onClick={addSecret} className="flex items-center gap-1.5 px-2.5 py-1.5 mt-2 text-[11px] rounded-[8px] text-[#64748b] hover:text-blue-700 border border-dashed border-[#e5e7eb] hover:border-blue-600 transition-colors">
                <Plus size={12} /> Create secret
              </button>
            </div>
          </SubSection>

          <SubSection title="Approvals/Review" description="Namespace/connector versions and their approval status">
            <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
              <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-[#475569]" style={{ background: '#f8fafc' }}>
                <span>Namespace</span>
                <span>Active Version</span>
                <span>Latest Approved</span>
                <span>Status</span>
                <span className="text-right">Action</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                {namespaces.map((ns) => (
                  <div key={ns.id}>
                    <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 items-center">
                      <span className="text-[11px] font-mono text-[#1e293b] truncate">{ns.namespace}</span>
                      <span className="text-[10px] text-[#475569]">{ns.sourceType === 'git' ? <span className="text-[#4f46e5]">from git</span> : ns.activeVersion}</span>
                      <span className="text-[10px] text-[#475569]">{ns.latestApproved}</span>
                      <span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={ns.status === 'APPROVED' ? { background: 'rgba(52,211,153,.15)', color: '#15803d' } : { background: 'rgba(251,146,60,.15)', color: '#c2410c' }}>
                          {ns.status}
                        </span>
                      </span>
                      <div className="flex justify-end">
                        {ns.reviewable && (
                          <button onClick={() => setReviewingId((cur) => (cur === ns.id ? null : ns.id))} className="px-2 py-1 rounded-[6px] text-[9px] font-semibold" style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}>
                            Review
                          </button>
                        )}
                      </div>
                    </div>
                    {reviewingId === ns.id && (
                      <div className="flex items-center gap-2 px-3 py-2" style={{ background: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
                        <span className="text-[10px] text-[#64748b] flex-1">Confirm {ns.namespace} {ns.activeVersion} for approval?</span>
                        <button onClick={() => approveNamespace(ns.id)} className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[9px] font-semibold" style={{ background: 'rgba(52,211,153,.12)', color: '#15803d' }}>
                          <Check size={10} /> Approve
                        </button>
                        <button onClick={() => rejectNamespace(ns.id)} className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-[9px] font-semibold" style={{ background: 'rgba(148,163,184,.12)', color: '#475569' }}>
                          <RotateCcw size={10} /> Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </SubSection>
        </div>
      )}
    </div>
  )
}
